import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../api/client';
import { getOnboardingStatusString } from '../../utils/stripeUtils';
import './OnboardingButton.css';

interface OnboardingButtonProps {
  className?: string;
}

const OnboardingButton: React.FC<OnboardingButtonProps> = ({ className }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [onboardingStatus, setOnboardingStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      try {
        const response = await apiClient.stripe.getOnboardingStatus();
        // Convert numeric enum value to string if needed
        setOnboardingStatus(getOnboardingStatusString(response.status));
      } catch (error) {
        console.error('Error fetching onboarding status:', error);
      }
    };

    if (currentUser?.stripeDetails?.stripeConnectedAccountId) {
      fetchOnboardingStatus();
    }
  }, [currentUser]);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      // Check for abandoned onboarding
      const abandonedCheck = await apiClient.stripe.checkAbandonedOnboarding();
      
      // Navigate to onboarding screen
      navigate('/onboarding');
    } catch (error) {
      console.error('Error navigating to onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser?.stripeDetails?.stripeConnectedAccountId) {
    return null;
  }

  let buttonText = 'Complete Account Onboarding Setup';
  let buttonVariant = 'primary';

  switch (onboardingStatus) {
    case 'Complete':
      buttonText = 'Account Setup Complete';
      buttonVariant = 'success';
      break;
    case 'InProgress':
      buttonText = 'Continue Account Setup';
      buttonVariant = 'warning';
      break;
    case 'Redo':
      buttonText = 'Update Account Information';
      buttonVariant = 'warning';
      break;
    default:
      buttonText = 'Complete Account Onboarding Setup';
      buttonVariant = 'primary';
  }

  return (
    <button
      className={`onboarding-button ${buttonVariant} ${className || ''}`}
      onClick={handleClick}
      disabled={isLoading || onboardingStatus === 'Complete'}
    >
      {isLoading ? 'Loading...' : buttonText}
    </button>
  );
};

export default OnboardingButton;